import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface WorkflowAutomation {
  id?: string;
  workflow_id?: string;
  stage_id: string;
  action_type: 'send_email' | 'create_task' | 'send_notification' | 'webhook';
  action_config: any;
  is_active: boolean;
}

export const useWorkflowAutomations = (workflowId: string | undefined) => {
  const [automations, setAutomations] = useState<WorkflowAutomation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (workflowId) {
      loadAutomations();
    } else {
      setAutomations([]);
      setLoading(false);
    }
  }, [workflowId]);

  const loadAutomations = async () => {
    if (!workflowId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('workflow_automations')
        .select('*')
        .eq('workflow_id', workflowId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      const typedData = (data || []).map(item => ({
        id: item.id,
        workflow_id: item.workflow_id,
        stage_id: item.stage_id,
        action_type: item.action_type as 'send_email' | 'create_task' | 'send_notification' | 'webhook',
        action_config: item.action_config,
        is_active: item.is_active,
      }));
      
      setAutomations(typedData);
    } catch (error) {
      console.error('Error loading automations:', error);
      toast.error('Failed to load automation rules');
    } finally {
      setLoading(false);
    }
  };

  const saveAutomations = async (workflowId: string, stageIds: string[], automationsToSave: WorkflowAutomation[]) => {
    try {
      // Delete existing automations for this workflow
      await supabase
        .from('workflow_automations')
        .delete()
        .eq('workflow_id', workflowId);

      // Insert new automations
      if (automationsToSave.length > 0) {
        const automationsWithIds = automationsToSave.map(automation => {
          // Find the actual stage ID from the provided stageIds array
          const stageIndex = parseInt(automation.stage_id);
          const actualStageId = !isNaN(stageIndex) && stageIndex < stageIds.length 
            ? stageIds[stageIndex] 
            : automation.stage_id;

          return {
            workflow_id: workflowId,
            stage_id: actualStageId,
            action_type: automation.action_type,
            action_config: automation.action_config,
            is_active: automation.is_active,
          };
        });

        const { error } = await supabase
          .from('workflow_automations')
          .insert(automationsWithIds);

        if (error) throw error;
      }

      toast.success('Automation rules saved');
      await loadAutomations();
      return true;
    } catch (error) {
      console.error('Error saving automations:', error);
      toast.error('Failed to save automation rules');
      return false;
    }
  };

  return {
    automations,
    loading,
    saveAutomations,
    reloadAutomations: loadAutomations,
  };
};
